/* import __COLOCATED_TEMPLATE__ from './fin-voice.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import { SideSheetKeys } from 'embercom/objects/workflows/graph-editor/editor-state';
import type FinVoice from 'embercom/models/operator/visual-builder/step/fin-voice';
import { type ComponentLike } from '@glint/template';
import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import { action } from '@ember/object';
import type FinVoiceStepConfig from 'embercom/objects/visual-builder/configuration/step/fin-voice';
import type Group from 'embercom/models/operator/visual-builder/group';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import { State } from 'embercom/models/data/matching-system/matching-constants';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Arguments {
  step: FinVoice;
  readOnly: boolean;
  editorState: EditorState;
  stepConfig?: FinVoiceStepConfig;
  connectionPointComponent: ComponentLike<{ connectionPoint: ConnectionPoint }>;
  isTemplatePreview?: boolean;
  group: Group; // Added group argument for step deletion
}

export default class FinVoiceNodeItem extends Component<Arguments> {
  @service declare appService: {
    app: {
      id: string;
    };
  };
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare usageLimitsService: any;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare attributeService: $TSFixMe;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked showDeleteFinVoiceModal = false;
  @tracked numberConfiguration?: FinVoiceNumberConfiguration;

  constructor(owner: unknown, args: Arguments) {
    super(owner, args);
    this.intl.maybeLoadMessengerTranslations();

    // Set the callback for the config menu to trigger the modal
    if (args.stepConfig) {
      args.stepConfig.triggerDeleteFinVoiceModalCallback =
        this.triggerDeleteFinVoiceModal.bind(this);
    }
    taskFor(this.loadFinUsageLimit).perform();
    taskFor(this.loadOrCreateFinVoiceNumberConfiguration).perform();
  }

  get shouldShowError() {
    return this.args.editorState.shouldShowValidations && !this.args.step.validations.isValid;
  }

  get sideSheetKeys() {
    return SideSheetKeys;
  }

  get showStepInvalidState() {
    return this.shouldShowError;
  }

  get triageBranch() {
    return this.args.step.outwardConnectionPoints.find((cp) => cp.type === 'fin_triage')!;
  }

  get usageLimitBreached() {
    return this.usageLimitsService.finHardLimitBreached;
  }

  get hasSideSheet() {
    return true;
  }

  get liveGuidelines() {
    return this.aiAgentGuidelineService.guidelines.filter(
      (guideline) => !guideline.isDeleted && guideline.state === State.Live,
    );
  }

  get liveGuidelinesCount() {
    return this.liveGuidelines.length;
  }

  get categoriesCount() {
    return this.attributeService.aiCategoriesDescriptors.filterBy('isEnabled', true).length;
  }

  get escalationRules() {
    return this.store.peekAll('ai-agent/escalation-rule').filterBy('enabled', true);
  }

  get hasEscalationItems() {
    return (
      this.liveGuidelines.filter((guideline) => guideline.category === 'routing').length +
        this.escalationRules.length >
      0
    );
  }

  get hasCsatEnabled() {
    return this.numberConfiguration?.handoverConfiguration?.askForCsatRating ?? false;
  }

  @task({ restartable: true })
  *loadFinUsageLimit(): TaskGenerator<void> {
    yield this.usageLimitsService.fetchFinUsageLimits();
  }

  @task({ restartable: true })
  *loadOrCreateFinVoiceNumberConfiguration(): TaskGenerator<void> {
    try {
      if (this.args.step.finVoiceNumberConfigurationId) {
        yield this.finVoiceConfigService.loadFinVoiceNumberConfigurationForStep(
          this.args.step.id,
          this.args.step.finVoiceNumberConfigurationId,
        );
      } else {
        let configId = yield this.finVoiceConfigService.createFinVoiceNumberConfigurationForStep(
          this.args.step.id,
          'ivr',
        );
        this.args.step.finVoiceNumberConfigurationId = configId;
      }
    } catch (error) {
      this.notifyFailure(error);
    } finally {
      this.numberConfiguration = this.finVoiceConfigService.getNumberConfigurationForStep(
        this.args.step.id,
      );
    }
  }

  @action
  triggerDeleteFinVoiceModal() {
    this.showDeleteFinVoiceModal = true;
  }

  @action
  hideDeleteFinVoiceModal() {
    this.showDeleteFinVoiceModal = false;
  }

  @action
  deleteFinVoiceStep() {
    this.args.editorState.deleteStep?.(this.args.step, this.args.group);
    this.showDeleteFinVoiceModal = false;
  }

  private notifyFailure(error: Error) {
    this.notificationsService.notifyResponseError(error, {
      default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinVoice': typeof FinVoiceNodeItem;
  }
}
